import {JwtPayloadType} from "@bounce/shared/contexts";
import {BounceRole} from "@bounce/shared/consumer/declarations";
import {jwtDecode} from "jwt-decode";
import React, {ReactNode, useEffect, useMemo} from "react";
import {Navigate, useLocation} from "react-router-dom";
import {useAuth} from "contexts";
import {useFlexUrlListener} from "hooks/useFlexUrlListener";
import {urls} from "../../urls";

type BackofficeGuardProps = {
  children: ReactNode;
};

const BackofficeGuard = ({children}: BackofficeGuardProps) => {
  const {search} = useLocation();
  const queryParams = new URLSearchParams(search);
  const agentTokenParam = queryParams.get("agentToken");

  const agentToken = useMemo<string>(() => {
    const token = agentTokenParam || localStorage.getItem("agentToken") || "";
    if (agentTokenParam && agentTokenParam !== localStorage.getItem("agentToken")) {
      localStorage.setItem("agentToken", agentTokenParam);
    }
    return token;
  }, [agentTokenParam]);

  const decodedToken: JwtPayloadType | undefined = useMemo(() => {
    if (agentToken) return jwtDecode(agentToken);
  }, [agentToken]);

  const agentEmail = useMemo<string>(() => {
    return localStorage.getItem("agentEmail") || (decodedToken && decodedToken.sub) || "";
  }, [localStorage, decodedToken]);

  const agentRole = useMemo<string>(() => {
    return localStorage.getItem("agentRole") || (decodedToken && decodedToken.role) || "";
  }, [localStorage, decodedToken]);

  const {credentials, setCredentials} = useAuth();

  useEffect(() => {
    if (agentEmail && !localStorage.getItem("agentEmail")) {
      localStorage.setItem("agentEmail", agentEmail);
    }
    if (agentRole && !localStorage.getItem("agentRole")) {
      localStorage.setItem("agentRole", agentRole);
    }
    setCredentials({token: agentToken, agentEmail, agentRole: agentRole as BounceRole});
  }, [agentToken, agentEmail]);

  useFlexUrlListener();

  if (!agentToken) {
    return <Navigate to={`${urls.backofficeLogin.url}?from=${encodeURIComponent(location.href)}`} />;
  }

  if (credentials?.token !== agentToken) {
    return null;
  }

  return <>{children}</>;
};

export default BackofficeGuard;
